import React, { useEffect, useMemo, useState } from 'react';
import { buildPortfolioSnapshot, buildRulesBasedCollectionsBrief } from '../utils/portfolioIntelligence';
import { generateCollectionsBrief, getAiBriefErrorMessage } from '../utils/aiCollectionsBrief';

function formatMoney(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));
}

function formatTimestamp(value) {
  if (!value) {
    return 'Just now';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Recently';
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getSourceBadge(brief) {
  if (brief?.mode === 'llm') {
    return {
      label: brief.sourceModel ? `Live AI brief (${brief.sourceModel})` : 'Live AI brief',
      tone: 'bg-white/15 text-white border border-white/20',
    };
  }

  return {
    label: 'Grounded rules preview',
    tone: 'bg-blue-100 text-blue-800 border border-blue-200',
  };
}

export default function AICollectionsBrief({ property, tenants, ervMap }) {
  const [generatedBrief, setGeneratedBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const snapshot = useMemo(() => buildPortfolioSnapshot({ property, tenants, ervMap }), [property, tenants, ervMap]);
  const fallbackBrief = useMemo(() => buildRulesBasedCollectionsBrief(snapshot), [snapshot]);
  const persistedBrief = property?.aiCollectionsBrief || null;
  const activeBrief = generatedBrief || persistedBrief || fallbackBrief;
  const sourceBadge = getSourceBadge(activeBrief);

  useEffect(() => {
    setGeneratedBrief(null);
    setError('');
  }, [property?.id]);

  if (!property || !activeBrief || snapshot.metrics.totalTenants === 0) {
    return null;
  }

  const handleGenerate = async () => {
    if (!property?.id) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const brief = await generateCollectionsBrief({
        propertyId: property.id,
        snapshot,
        baselineBrief: fallbackBrief,
      });
      setGeneratedBrief(brief);
    } catch (requestError) {
      setError(getAiBriefErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mt-6 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
      <div className="bg-gradient-to-r from-[#0B1F3B] via-[#123264] to-[#2563EB] px-6 py-6 text-white">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-100">Predictive Intelligence</span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${sourceBadge.tone}`}>{sourceBadge.label}</span>
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight">{activeBrief.headline}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-blue-100">{activeBrief.executiveSummary}</p>
          </div>

          <div className="flex flex-col items-start gap-2 lg:items-end">
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Generating...' : activeBrief.mode === 'llm' ? 'Refresh AI Brief' : 'Generate Live AI Brief'}
            </button>
            <p className="text-xs text-blue-100">Updated {formatTimestamp(activeBrief.generatedAt)}</p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-100">Next 7 days</p>
            <p className="mt-2 text-2xl font-bold">{formatMoney(activeBrief.predictedCollection?.next7Days)}</p>
            <p className="mt-1 text-xs text-blue-100">Near-term collection potential</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-100">Next 30 days</p>
            <p className="mt-2 text-2xl font-bold">{formatMoney(activeBrief.predictedCollection?.next30Days)}</p>
            <p className="mt-1 text-xs text-blue-100">{activeBrief.predictedCollection?.basis}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-100">Confidence</p>
            <p className="mt-2 text-2xl font-bold capitalize">{activeBrief.predictedCollection?.confidence || snapshot.dataQuality.confidence}</p>
            <p className="mt-1 text-xs text-blue-100">{activeBrief.confidenceRationale}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 px-6 py-6 lg:grid-cols-[1.25fr,0.95fr]">
        <div className="space-y-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Portfolio Story</p>
            <p className="mt-3 text-sm leading-7 text-slate-700">{activeBrief.portfolioStory}</p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Top Actions</p>
            <div className="mt-3 grid gap-3">
              {(activeBrief.topActions || []).map((action) => (
                <div key={`${action.title}-${action.timing}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-900">{action.title}</p>
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {action.timing}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{action.reason}</p>
                  <p className="mt-2 text-xs font-medium text-blue-700">{action.impact}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Priority Tenants</p>
            <div className="mt-3 space-y-3">
              {(activeBrief.priorityTenants || []).map((tenant) => (
                <div key={`${tenant.tenantId}-${tenant.name}`} className="rounded-2xl bg-slate-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{tenant.name}</p>
                      <p className="text-xs text-slate-500">{tenant.unit ? `Unit ${tenant.unit}` : 'Unit not set'}</p>
                    </div>
                    {'ervDollars' in tenant && (
                      <span className="text-xs font-semibold text-blue-700">{formatMoney(tenant.ervDollars)}</span>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-600">{tenant.reason}</p>
                  <p className="mt-2 text-xs font-medium text-slate-800">{tenant.play || tenant.recommendedAction}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1">
            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Blind Spots</p>
              <div className="mt-3 space-y-2">
                {(activeBrief.blindSpots || []).map((item) => (
                  <p key={item} className="text-sm text-slate-600">{item}</p>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Opportunities</p>
              <div className="mt-3 space-y-2">
                {(activeBrief.opportunities || []).map((item) => (
                  <p key={item} className="text-sm text-slate-600">{item}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {(activeBrief.operatorQuestions?.length > 0 || error) && (
        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
          {activeBrief.operatorQuestions?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Questions to Close Tomorrow</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {activeBrief.operatorQuestions.map((item) => (
                  <span key={item} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {error}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
